import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ImpersonatedUser {
  id: string;
  email: string;
  full_name: string | null;
}

export function useImpersonation() {
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(() => {
    const stored = localStorage.getItem('impersonated_user');
    return stored ? JSON.parse(stored) : null;
  });
  const { toast } = useToast();

  const startImpersonation = useCallback(async (userId: string) => {
    try {
      // Fetch user details
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name')
        .eq('id', userId)
        .single();

      if (error) throw error;

      const user: ImpersonatedUser = {
        id: profile.id,
        email: profile.email,
        full_name: profile.first_name && profile.last_name 
          ? `${profile.first_name} ${profile.last_name}` 
          : profile.email,
      };

      setImpersonatedUser(user);
      localStorage.setItem('impersonated_user', JSON.stringify(user));

      toast({
        title: "Impersonation Started",
        description: `Now viewing as: ${user.full_name || user.email}`,
      });

      // Reload to apply impersonation
      window.location.reload();
    } catch (error) {
      console.error('Error starting impersonation:', error);
      toast({
        title: "Error",
        description: "Failed to start impersonation",
        variant: "destructive",
      });
    }
  }, [toast]);

  const stopImpersonation = useCallback(() => {
    setImpersonatedUser(null);
    localStorage.removeItem('impersonated_user');
    
    toast({
      title: "Impersonation Ended",
      description: "Returned to your admin account",
    });

    // Reload to remove impersonation
    window.location.reload();
  }, [toast]);

  return {
    impersonatedUser,
    isImpersonating: !!impersonatedUser,
    startImpersonation,
    stopImpersonation,
  };
}
